import React, { useState, useRef, useEffect } from "react";
import { IonButton, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";

interface ImagePair {
    left_id: number;
    right_id: number;
    leftImage: string;
    rightImage: string;
}



const wordList = ["cat", "dog", "bat", "hat", "cap", "pen", "sun", "net", "fan", "bag", "jug", "pan", "box", "zip", "bus", "mat", "rat", "log", "mop", "fox", "hen", "pig", "fig", "bin", "lip", "lid", "web"];


const MatchWords: React.FC = () => {
    const [connections, setConnections] = useState<{ x1: number; y1: number; x2: number; y2: number; correct: boolean }[]>([]);
    const [selectedLeft, setSelectedLeft] = useState<{ id: number; x: number; y: number } | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [totalQuestion, setTotalQuestion] = useState<any>([]);
    const [showImage, setShowImage] = useState(false);
    const [question, setQuestion] = useState('');
    const [questions, setQuestions] = useState<ImagePair[]>([]);
    const [height, setHeight] = useState('0px');


    const modal = useRef<HTMLIonModalElement>(null);


    // Get element position relative to .match-container
    const getRelativePosition = (element: HTMLDivElement) => {
        const container = containerRef.current;
        if (!container) return { x: 0, y: 0 };

        const elementRect = element.getBoundingClientRect();
        setHeight((container.scrollHeight) + 'px');

        const screenWidth = window.innerWidth;
        let elementX, elementY;
        if (screenWidth >= 1024) {
            elementX = element.offsetLeft + elementRect.width;
            elementY = element.offsetTop + elementRect.height / 2;
        }
        else {
            elementX = element.offsetLeft + elementRect.width / 2;
            elementY = element.offsetTop + elementRect.height / 2;
        }

        return {
            x: elementX,
            y: elementY
        }
    };

    // Handle left image click
    const handleLeftClick = (event: React.MouseEvent<HTMLDivElement>, id: number) => {
        const position = getRelativePosition(event.currentTarget);
        setSelectedLeft({ id, x: position.x, y: position.y });
    };

    // Handle right image click and draw SVG line
    const handleRightClick = (event: React.MouseEvent<HTMLDivElement>, rightId: number) => {
        if (selectedLeft) {
            const position = getRelativePosition(event.currentTarget);

            // Check if the connection is correct
            const isCorrect = questions.some(pair => selectedLeft.id === rightId);

            setConnections([...connections, { x1: selectedLeft.x, y1: selectedLeft.y, x2: position.x, y2: position.y, correct: isCorrect }]);
            setSelectedLeft(null);
        }
    };

    // Reset connections
    const resetConnections = () => {
        setConnections([]);
        setSelectedLeft(null);
    };

    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            // Generate a random index
            const randomIndex = Math.floor(Math.random() * (i + 1));
            // Swap elements
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    // Fetch question data from an external source (replace with your actual API or JSON URL)
    const fetchQuestions = async () => {

        const shuffledWords = [...wordList].sort(() => Math.random() - 0.5);

        const selectedWords = shuffledWords.slice(0, 4);
        const selectedWordImages = selectedWords.map(word => word);
        const shuffledSelectedWordImages = shuffleArray(selectedWordImages);

        //console.log(shuffledSelectedWordImages)


        const pairs = selectedWords.map((letter, index) => {

            return {
                left_id: index + 1,
                leftImage: letter,
                rightImage: shuffledSelectedWordImages[index],
                right_id: selectedWords.indexOf(shuffledSelectedWordImages[index]) + 1
            };
        });

        //console.log(pairs)

        setQuestions(pairs);
    };

    useEffect(() => {
        resetConnections()
        fetchQuestions();
        document.title = "Match the Phonetic Word – LKG Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    const nextQuestion = () => {
        console.log('next question')
        resetConnections()
        setShowImage(true);
        setTimeout(() => {
            fetchQuestions();
            setShowImage(false);
        }, 3000);
    }


    return (
        <>
            {
                showImage === false ?
                    <>
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Match the Phonetic Word</h1>

                                <p>
                                    Let your child master the sounds of letters and build early reading skills with this <strong>Match the Phonetic Word</strong> worksheet! Specially designed for LKG learners, this activity helps children <strong>match simple CVC words</strong> (like <b><em>cat, dog, pin, sun</em></b>) with corresponding pictures.
                                </p>

                                <h2>🔡 What’s in the Worksheet?</h2>
                                <p>
                                    The worksheet features a set of <strong>phonetic (CVC) words</strong> on one side and <strong>pictures</strong> on the other. Your child will draw lines to match each word with the correct image, improving their <strong>word recognition and decoding skills</strong>.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Recognize and read <strong>CVC (Consonant-Vowel-Consonant)</strong> words</li>
                                    <li>Strengthen phonics and letter-sound understanding</li>
                                    <li>Match words to visual meanings using phonetic clues</li>
                                    <li>Boost <strong>vocabulary, attention, and comprehension</strong></li>
                                </ul>

                                <h2>👩‍🏫 How to Use the Worksheet</h2>
                                <ul>
                                    <li>Review the phonetic words with your child, sounding out each one.</li>
                                    <li>Help them pronounce and decode the words.</li>
                                    <li>Ask them to match the words with the correct pictures.</li>
                                    <li>Reinforce learning with <strong>real-life object examples</strong> if available.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/english-language/one-and-many"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>One and Many</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/english-language/rhyming-words"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Rhyming Words</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/english-language/opposites"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Opposites</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Activity Is Important</h2>
                                <p>
                                    Matching phonetic words to pictures helps young children <strong>link sound with meaning</strong>. It encourages them to sound out, read, and understand words — a key step in becoming confident readers.
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Match the Phonetic Word</h1>

                            <p>
                                Let your child master the sounds of letters and build early reading skills with this <strong>Match the Phonetic Word</strong> worksheet! Specially designed for LKG learners, this activity helps children <strong>match simple CVC words</strong> (like <em>cat, dog, pin, sun</em>) with corresponding pictures.
                            </p>

                            <h2>🔡 What’s in the Worksheet?</h2>
                            <p>
                                The worksheet features a set of <strong>phonetic (CVC) words</strong> on one side and <strong>pictures</strong> on the other. Your child will draw lines to match each word with the correct image, improving their <strong>word recognition and decoding skills</strong>.
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Recognize and read CVC (Consonant-Vowel-Consonant) words</li>
                                <li>Strengthen phonics and letter-sound understanding</li>
                                <li>Match words to visual meanings using phonetic clues</li>
                                <li>Boost vocabulary, attention, and comprehension</li>
                            </ul>

                            <h2>👩‍🏫 How to Use the Worksheet</h2>
                            <ul>
                                <li>Review the phonetic words with your child, sounding out each one.</li>
                                <li>Help them pronounce and decode the words.</li>
                                <li>Ask them to match the words with the correct pictures.</li>
                                <li>Reinforce learning with real-life object examples if available.</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/lkg/english-language/one-and-many"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>One and Many</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/english-language/rhyming-words"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Rhyming Words</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/english-language/opposites"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Opposites</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Why This Activity Is Important</h2>
                            <p>
                                Matching phonetic words to pictures helps young children <strong>link sound with meaning</strong>. It encourages them to sound out, read, and understand words — a key step in becoming confident readers.
                            </p>
                        </article>
                        <div ref={containerRef} className="contentPart additionWord matchImage match-container" style={{ position: "relative", marginTop: "0%", paddingTop: "0%", height: "calc(-200px + 100vh)" }}>
                            {/* SVG for drawing lines */}
                            <svg ref={svgRef} className="connector-svg" height={height} style={{ position: "absolute", top: 0, left: 0, width: "100%", pointerEvents: "none", zIndex: 10 }}>
                                {connections.map((conn, index) => (
                                    <line
                                        key={index}
                                        x1={conn.x1}
                                        y1={conn.y1}
                                        x2={conn.x2}
                                        y2={conn.y2}
                                        stroke={conn.correct ? "green" : "red"} // Green if correct, Red if incorrect
                                        strokeWidth="3"
                                    />
                                ))}
                            </svg>


                            {/* <h6>Match the Capital Alphabet to its Small Alphabet</h6> */}
                            {/* Left & Right columns under SVG */}
                            <div className="mb-2 columns" style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 5, width: "100%", height: "100%" }}>
                                <div className="left-column">
                                    {questions.map((item) => (
                                        <div
                                            key={item.left_id}
                                            className={`image-box ${selectedLeft?.id === item.left_id ? "selected" : ""}`}
                                            onClick={(e) => handleLeftClick(e, item.left_id)}
                                            style={{ cursor: "pointer", padding: "10px" }}
                                        >
                                            {item.leftImage}
                                        </div>
                                    ))}
                                </div>

                                <div className="right-column">
                                    {questions.map((item) => (
                                        <div key={item.left_id} className="image-box" onClick={(e) => handleRightClick(e, item.right_id)} style={{ cursor: "pointer", padding: "10px" }}>
                                            <img src={process.env.REACT_APP_API_URL + item.rightImage + '.png'} />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="mt-2 mb-4 text-center" style={{ display: 'flex', gap: '10px' }}>
                                <div className="optionButton background-blue" style={{ fontSize: '18px' }} onClick={resetConnections}>
                                    Reset
                                </div>
                                <div className="btn hero-btn" style={{ fontSize: '18px' }} onClick={() => nextQuestion()}>Next &gt;&gt;</div>
                            </div>

                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default MatchWords;
